// Shared head() builder for /blog/* articles.
// Every blog article shares the same Article + FAQPage + BreadcrumbList + WebPage
// JSON-LD shape; only the copy varies. Centralising here removes ~90 lines per
// article file.
import { buildFaqPageSchema } from "@/lib/faqSchema";
import { publicAuthorName, publicAuthorUrl } from "@/lib/blog/author";

export type BlogFaq = { q: string; a: string };

export type BlogMeta = {
  slug: string; // e.g. "berberine-guide"
  title: string; // <title> includes "| TruWe" suffix
  description: string;
  ogTitle: string; // OG variant, usually the headline without brand suffix
  ogDescription: string;
  headline: string; // schema.org "headline" the shorter article title
  breadcrumbName: string; // BreadcrumbList final segment name
  datePublished: string; // ISO date
  dateModified: string; // ISO date
  faqs: BlogFaq[];
  ogImage?: string; // absolute URL, defaults to /og-image.jpg
  /** schema.org type — CMS articles emit BlogPosting, coded guides stay Article. */
  articleType?: "Article" | "BlogPosting";
  authorName?: string;
  authorUrl?: string;
  /** Only emitted when a reviewer is actually credited on the visible page. */
  reviewerName?: string;
  reviewerCredentials?: string;
  reviewerUrl?: string;
  reviewedAt?: string;
};


const SITE = "https://truwe.in";
const DEFAULT_OG_IMAGE = `${SITE}/og-image.jpg`;
const PUBLISHER = {
  "@type": "Organization",
  name: "TruWe",
  logo: {
    "@type": "ImageObject",
    url: `${SITE}/__l5e/assets-v1/ede5b935-c43f-48c9-9c92-a6088557696b/truwe-logo-precision-v2.png`,
    width: 512,
    height: 512,
  },
} as const;

export function buildBlogHead(m: BlogMeta) {
  const url = `${SITE}/blog/${m.slug}`;
  const image = m.ogImage ?? DEFAULT_OG_IMAGE;
  const author = {
    "@type": "Organization",
    name: publicAuthorName(m.authorName),
    url: publicAuthorUrl(m.authorUrl) ?? SITE,
  };
  // Reviewer entities are only emitted when the page visibly credits a
  // reviewer, so structured data never claims a review that isn't shown.
  const reviewer = m.reviewerName
    ? {
        reviewedBy: {
          "@type": "Person",
          name: m.reviewerName,
          ...(m.reviewerCredentials ? { jobTitle: m.reviewerCredentials } : {}),
          ...(m.reviewerUrl ? { url: m.reviewerUrl } : {}),
        },
        ...(m.reviewedAt ? { lastReviewed: m.reviewedAt } : {}),
      }
    : {};
  return {
    meta: [
      { title: m.title },
      { name: "description", content: m.description },
      { property: "og:title", content: m.ogTitle },
      { property: "og:description", content: m.ogDescription },
      { property: "og:type", content: "article" },
      { property: "og:url", content: url },
      { property: "og:image", content: image },
    ],
    links: [{ rel: "canonical", href: url }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": m.articleType ?? "Article",
          "@id": `${url}#article`,
          headline: m.headline,
          description: m.description,
          image: [image],
          author,
          publisher: PUBLISHER,
          datePublished: m.datePublished,
          dateModified: m.dateModified,
          ...reviewer,
          mainEntityOfPage: { "@type": "WebPage", "@id": url },
          inLanguage: "en-IN",
        }),
      },

      // FAQPage is only valid when the page actually renders the Q&A.
      ...(m.faqs.length
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify(
                buildFaqPageSchema({
                  pageUrl: url,
                  datePublished: m.datePublished,
                  dateModified: m.dateModified,
                  faqs: m.faqs,
                }),
              ),
            },
          ]
        : []),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
            { "@type": "ListItem", position: 2, name: m.breadcrumbName, item: url },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": url,
          url,
          name: m.headline,
          description: m.description,
          isPartOf: { "@type": "WebSite", url: SITE },
          primaryImageOfPage: { "@type": "ImageObject", url: image },
          mainEntity: { "@id": `${url}#article` },
          inLanguage: "en-IN",
        }),
      },
    ],
  };
}
